"use client";

import * as PopoverPrimitives from "@radix-ui/react-popover";

import React, { useCallback, useMemo, useState } from "react";
import { RiArrowRightSLine, RiCheckLine } from "react-icons/ri";

import {
  SearchSelect,
  SearchSelectContent,
  SearchSelectItem,
  SearchSelectTrigger,
  SearchSelectValue,
  useSearchSelect,
} from "@/components/ui/select/search-select";

import {
  formatDurationRange,
  serviceDurationRange,
} from "@/lib/provider-service-duration";
import { cx } from "@/lib/tailwind-utils";

import type { ServiceDetail } from "@/types/service";

interface ServiceGroup {
  label: string;
  services: ServiceDetail[];
  /** Services the provider isn't assigned to start folded away */
  collapsedByDefault?: boolean;
}

const sortByName = (a: ServiceDetail, b: ServiceDetail) =>
  a.name.localeCompare(b.name);

function isServiceAtLocation(
  service: ServiceDetail,
  locationId?: string,
): boolean {
  if (!service.locationIds?.length) return true;
  return !!locationId && service.locationIds.includes(locationId);
}

function computeServiceGroups(
  services: ServiceDetail[],
  assignedServiceIds: Set<string> | null,
  locationId?: string,
): ServiceGroup[] {
  const result: ServiceGroup[] = [];

  if (assignedServiceIds && locationId) {
    // 4-way grouping: assigned x location
    const assignedHere: ServiceDetail[] = [];
    const assignedElsewhere: ServiceDetail[] = [];
    const otherHere: ServiceDetail[] = [];
    const otherElsewhere: ServiceDetail[] = [];

    for (const s of services) {
      const assigned = assignedServiceIds.has(s.id);
      const here = isServiceAtLocation(s, locationId);
      if (assigned && here) assignedHere.push(s);
      else if (assigned && !here) assignedElsewhere.push(s);
      else if (!assigned && here) otherHere.push(s);
      else otherElsewhere.push(s);
    }

    if (assignedHere.length)
      result.push({
        label: "Assigned to provider \u00b7 This location",
        services: assignedHere.sort(sortByName),
      });
    if (assignedElsewhere.length)
      result.push({
        label: "Assigned to provider \u00b7 Other locations",
        services: assignedElsewhere.sort(sortByName),
      });
    if (otherHere.length)
      result.push({
        label: "Other services \u00b7 This location",
        services: otherHere.sort(sortByName),
        collapsedByDefault: true,
      });
    if (otherElsewhere.length)
      result.push({
        label: "Other services \u00b7 Other locations",
        services: otherElsewhere.sort(sortByName),
        collapsedByDefault: true,
      });
  } else if (assignedServiceIds) {
    // No location - group by assignment only
    const assigned: ServiceDetail[] = [];
    const other: ServiceDetail[] = [];

    for (const s of services) {
      if (assignedServiceIds.has(s.id)) assigned.push(s);
      else other.push(s);
    }

    if (assigned.length)
      result.push({
        label: "Assigned to provider",
        services: assigned.sort(sortByName),
      });
    if (other.length)
      result.push({
        label: "Other services",
        services: other.sort(sortByName),
        collapsedByDefault: true,
      });
  } else if (locationId) {
    // No assignment - group by location only
    const here: ServiceDetail[] = [];
    const elsewhere: ServiceDetail[] = [];

    for (const s of services) {
      if (isServiceAtLocation(s, locationId)) here.push(s);
      else elsewhere.push(s);
    }

    if (here.length)
      result.push({
        label: "Available at this location",
        services: here.sort(sortByName),
      });
    if (elsewhere.length)
      result.push({
        label: "Not available at this location",
        services: elsewhere.sort(sortByName),
      });
  } else {
    // No grouping needed
    result.push({ label: "", services: [...services] });
  }

  return result;
}

/** Renders the inner content of a service item (shared between flat and grouped) */
function ServiceItemContent({
  svc,
  isAlreadySelected,
}: {
  svc: ServiceDetail;
  isAlreadySelected: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {svc.color && (
          <div
            className="h-3 w-3 flex-shrink-0 rounded-sm"
            style={{ backgroundColor: svc.color }}
          />
        )}
        <span
          className={cx(
            "line-clamp-2 whitespace-normal",
            isAlreadySelected
              ? "text-foreground-muted"
              : "font-medium text-foreground",
          )}
        >
          {svc.name}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-caption text-muted-foreground">
          {`${formatDurationRange(serviceDurationRange(svc).min, serviceDurationRange(svc).max)} mins`}
        </span>
        {isAlreadySelected ? (
          <RiCheckLine className="size-5 shrink-0 text-foreground" />
        ) : (
          <div className="size-5 shrink-0" />
        )}
      </div>
    </div>
  );
}

/**
 * Inner component that handles search-aware grouped rendering.
 * Must be rendered inside a SearchSelect context.
 */
function GroupedServiceList({
  groups,
  selectedServiceIds,
  allowDuplicateSelections,
  emptyMessage,
}: {
  groups: ServiceGroup[];
  selectedServiceIds?: string[];
  allowDuplicateSelections?: boolean;
  emptyMessage: string;
}) {
  const { searchTerm } = useSearchSelect();
  const [expandedLabels, setExpandedLabels] = useState<Record<string, boolean>>(
    {},
  );
  // Nothing to fold away when the provider has no assigned services at all.
  const canCollapse = groups.some((group) => !group.collapsedByDefault);

  const filteredGroups = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        services: term
          ? group.services.filter((svc) =>
              svc.name.toLowerCase().includes(term),
            )
          : group.services,
      }))
      .filter((group) => group.services.length > 0);
  }, [groups, searchTerm]);

  if (filteredGroups.length === 0) {
    return (
      <div className="px-3 py-2 text-body text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {filteredGroups.map((group) => {
        const collapsible =
          canCollapse && !!group.collapsedByDefault && !searchTerm;
        const expanded = !collapsible || !!expandedLabels[group.label];
        return (
          <div key={group.label || "ungrouped"}>
            {group.label &&
              (collapsible ? (
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedLabels((prev) => ({
                      ...prev,
                      [group.label]: !prev[group.label],
                    }))
                  }
                  className="flex w-full items-center gap-1 rounded-lg px-3 py-2 text-left text-caption-strong tracking-wide text-muted-foreground hover:bg-hover"
                >
                  <RiArrowRightSLine
                    className={cx(
                      "size-4 shrink-0 transition-transform",
                      expanded && "rotate-90",
                    )}
                    aria-hidden
                  />
                  <span className="flex-1">{group.label}</span>
                  <span>{group.services.length}</span>
                </button>
              ) : (
                <div className="px-3 py-2 text-caption-strong tracking-wide text-muted-foreground">
                  {group.label}
                </div>
              ))}
            {expanded &&
              group.services.map((svc) => {
                const isAlreadySelected =
                  !!selectedServiceIds?.includes(svc.id) &&
                  !(allowDuplicateSelections && svc.allowMultipleBookings);
                return (
                  <SearchSelectItem
                    key={svc.id}
                    value={svc.id}
                    showCheckmark={false}
                    className={cx(
                      isAlreadySelected &&
                        "pointer-events-none text-foreground-muted",
                    )}
                  >
                    <ServiceItemContent
                      svc={svc}
                      isAlreadySelected={isAlreadySelected}
                    />
                  </SearchSelectItem>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

interface ServiceSearchSelectProps {
  /** Available services to display */
  services: ServiceDetail[];
  /** Currently selected service ID */
  value?: string;
  /** Called when a service is selected */
  onValueChange: (serviceId: string) => void;
  /** Service IDs that should appear greyed-out/disabled (already selected elsewhere) */
  selectedServiceIds?: string[];
  /** Allow duplicate selection for services that opt in via allowMultipleBookings */
  allowDuplicateSelections?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  triggerClassName?: string;
  /** Called when the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Custom trigger element. When provided, renders this as the popover trigger instead of the default select trigger. */
  children?: React.ReactNode;
  /** When provided, services are grouped by assignment status (assigned vs other) */
  assignedServiceIds?: Set<string>;
  /** When provided, services are additionally grouped by location match */
  groupByLocationId?: string;
}

export function ServiceSearchSelect({
  services,
  value,
  onValueChange,
  selectedServiceIds,
  allowDuplicateSelections = false,
  placeholder = "Select service...",
  searchPlaceholder = "Search services...",
  emptyMessage = "No services found",
  disabled,
  hasError,
  errorMessage,
  triggerClassName,
  onOpenChange,
  children,
  assignedServiceIds,
  groupByLocationId,
}: ServiceSearchSelectProps) {
  const serviceNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const svc of services) {
      map.set(svc.id, svc.name);
    }
    return map;
  }, [services]);

  const formatValue = useCallback(
    (id: string) => serviceNameMap.get(id) ?? id,
    [serviceNameMap],
  );

  const groups = useMemo(
    () =>
      computeServiceGroups(
        services,
        assignedServiceIds ?? null,
        groupByLocationId,
      ),
    [services, assignedServiceIds, groupByLocationId],
  );

  return (
    <SearchSelect
      value={value ?? ""}
      onValueChange={disabled ? undefined : onValueChange}
      onOpenChange={onOpenChange}
    >
      {children ? (
        <PopoverPrimitives.Trigger asChild>
          {children}
        </PopoverPrimitives.Trigger>
      ) : (
        <SearchSelectTrigger
          hasError={hasError}
          errorMessage={errorMessage}
          disabled={disabled}
          className={triggerClassName}
        >
          <SearchSelectValue
            placeholder={placeholder}
            formatValue={formatValue}
          />
        </SearchSelectTrigger>
      )}
      <SearchSelectContent
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        className="w-96 min-w-0 max-w-[calc(100vw-2rem)]"
      >
        <GroupedServiceList
          groups={groups}
          selectedServiceIds={selectedServiceIds}
          allowDuplicateSelections={allowDuplicateSelections}
          emptyMessage={emptyMessage}
        />
      </SearchSelectContent>
    </SearchSelect>
  );
}
